import React, { ReactNode } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import Pagination from "components/common/Pagination";
import "./DataTable.scss";

export interface ColumnDef<T> {
  id: string;
  label: string;
  width?: number | string;
  align?: "left" | "center" | "right";
  render: (row: T) => ReactNode;
}

export interface DataTablePaginationProps {
  page: number;
  count: number;
  rowsPerPage: number;
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  onRowsPerPageChange: (event: SelectChangeEvent<number>) => void;
}

interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  emptyText?: string;
  stickyHeader?: boolean;
  maxHeight?: string;
  pagination?: DataTablePaginationProps;
}

function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyText = "ჩანაწერები არ მოიძებნა",
  stickyHeader = true,
  maxHeight = "calc(100vh - 260px)",
  pagination,
}: DataTableProps<T>) {
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
      {loading && <LinearProgress sx={{ height: 2 }} />}
      <TableContainer sx={{ maxHeight }}>
        <Table stickyHeader={stickyHeader} size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  className="data-table__th"
                  align={col.align ?? "left"}
                  width={col.width}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyText}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={rowKey(row)} hover sx={{ "&:last-child td": { border: 0 } }}>
                  {columns.map((col) => (
                    <TableCell key={col.id} align={col.align ?? "left"}>
                      {col.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <Pagination
          count={pagination.count}
          page={pagination.page}
          onChange={pagination.onPageChange}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={pagination.onRowsPerPageChange}
        />
      )}
    </Paper>
  );
}

export default DataTable;
